import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { clientHeaders } from "./client-access";
import { APIResponse, APIError } from "@/types";

// Extend axios config to include metadata for timing
declare module "axios" {
  export interface InternalAxiosRequestConfig {
    metadata?: { startTime: number };
  }
}

// API client configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";
const API_TIMEOUT = 30000; // 30 seconds

class APIClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        Object.assign(config.headers, clientHeaders());

        // Add request timestamp for debugging
        config.metadata = { startTime: Date.now() };

        console.log(
          `🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`,
          {
            params: config.params,
          },
        );

        return config;
      },
      (error) => {
        console.error("❌ Request Error:", error);
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse<APIResponse>) => {
        const duration =
          Date.now() - (response.config.metadata?.startTime || 0);

        console.log(
          `✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`,
          {
            status: response.status,
            duration: `${duration}ms`,
          },
        );

        return response;
      },
      async (error) => {
        const duration = Date.now() - (error.config?.metadata?.startTime || 0);

        console.error(
          `❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
          {
            status: error.response?.status,
            duration: `${duration}ms`,
            error: error.response?.data || error.message,
          },
        );

        // Handle 401 - Unauthorized
        if (error.response?.status === 401) {
          this.clearToken();
          // Redirect to login or emit auth error event
          window.dispatchEvent(new CustomEvent("auth:unauthorized"));
        }

        // Handle 403 - Forbidden
        if (error.response?.status === 403) {
          window.dispatchEvent(new CustomEvent("auth:forbidden"));
        }

        // Handle network errors
        if (!error.response) {
          const networkError: APIError = {
            message: "Network error. Please check your connection.",
            code: "NETWORK_ERROR",
            details: { originalError: error.message },
          };
          return Promise.reject(networkError);
        }

        // Transform API error response
        const apiError: APIError = {
          message:
            error.response.data?.message || "An unexpected error occurred",
          code: error.response.data?.code || `HTTP_${error.response.status}`,
          details: error.response.data?.details || {},
        };

        return Promise.reject(apiError);
      },
    );
  }

  // Token management
  setToken(token: string) {
    this.token = token;
    localStorage.setItem("auth_token", token);

    // Also set token in axios headers immediately
    this.client.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem("auth_token");
    }
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem("auth_token");

    // Also remove token from axios headers
    delete this.client.defaults.headers.common["Authorization"];
  }

  // Generic request method
  async request<T = any>(config: AxiosRequestConfig): Promise<APIResponse<T>> {
    const response = await this.client.request<APIResponse<T>>(config);
    return response.data;
  }

  // HTTP methods
  async get<T = any>(url: string, params?: any): Promise<APIResponse<T>> {
    return this.request<T>({
      method: "GET",
      url,
      params,
    });
  }

  async post<T = any>(url: string, data?: any): Promise<APIResponse<T>> {
    return this.request<T>({
      method: "POST",
      url,
      data,
    });
  }

  async put<T = any>(url: string, data?: any): Promise<APIResponse<T>> {
    return this.request<T>({
      method: "PUT",
      url,
      data,
    });
  }

  async patch<T = any>(url: string, data?: any): Promise<APIResponse<T>> {
    return this.request<T>({
      method: "PATCH",
      url,
      data,
    });
  }

  async delete<T = any>(url: string, data?: unknown): Promise<APIResponse<T>> {
    return this.request<T>({
      method: "DELETE",
      url,
      data,
    });
  }

  // File upload method
  async upload<T = any>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<APIResponse<T>> {
    const formData = new FormData();
    formData.append("file", file);

    return this.request<T>({
      method: "POST",
      url,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(progress);
        }
      },
    });
  }

  // Download method
  async download(url: string, filename?: string): Promise<void> {
    try {
      const response = await this.client.get(url, {
        responseType: "blob",
      });

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download failed:", error);
      throw error;
    }
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      await this.get("/health");
      return true;
    } catch (error) {
      return false;
    }
  }
}

// Create and export singleton instance
export const apiClient = new APIClient();

// Export class for testing
export { APIClient };
